import React, { useState } from "react";
import createInstance from "../../api/axiosinstance";
import { toast } from "react-toastify";
import { IconButton } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const InterviewerJoining = () => {
  const aiDishaApi = createInstance("DISHA");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    // stored as YYYY-MM-DD from <input type="date">
    Date_of_birth: "",
    address: {
      H_No: "",
      city: "",
      state: "",
      country: "",
    },
    password: "",
    confirm_password: "",
    role: "TP-Interviewer",
    status: "InActive",
    activation_status: "Pending",
  });

  // today's date in YYYY-MM-DD (for max attr & validation)
  const todayStr = new Date().toISOString().split("T")[0];

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (["H_No", "city", "state", "country"].includes(name)) {
      setFormData((prev) => ({
        ...prev,
        address: { ...prev.address, [name]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Validate form inputs (includes 18+ and <120, no future date, mobile 6–9)
  const validateForm = () => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    // Indian mobile: starts with 6/7/8/9 and 10 digits
    const phoneRegex = /^[6-9]\d{9}$/;

    if (!formData.name.trim()) return "Name is required";
    if (!formData.email || !emailRegex.test(formData.email))
      return "Enter a valid email";
    if (!formData.mobile || !phoneRegex.test(formData.mobile))
      return "Enter a valid 10-digit Indian mobile number starting with 6, 7, 8, or 9";

    // DOB validations (YYYY-MM-DD from <input type='date'>)
    // Inside validateForm()
    if (!formData.Date_of_birth) return "Date of birth is required";

    const dob = new Date(formData.Date_of_birth);
    const today = new Date();

    // Invalid date check
    if (isNaN(dob.getTime())) {
      return "Enter a valid date of birth";
    }

    // Future date check
    if (dob > today) {
      return "Date of birth cannot be in the future";
    }

    // Age calculation
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }

    if (age < 18) {
      return "You must be at least 18 years old to register as an interviewer";
    }
    if (age >= 120) {
      return "Date of birth is not valid. Age cannot be 120 years or above.";
    }
    if (!formData.address.H_No.trim()) return "House No. is required";
    if (!formData.address.city.trim()) return "City is required";
    if (!formData.address.state.trim()) return "State is required";
    if (!formData.address.country.trim()) return "Country is required";

    if (!formData.password) return "Password is required";
    if (formData.password.length < 6)
      return "Password must be at least 6 characters";
    if (formData.password !== formData.confirm_password)
      return "Passwords do not match";

    return null;
  };

  // Clear form
  const clearForm = () => {
    setFormData({
      name: "",
      email: "",
      mobile: "",
      Date_of_birth: "",
      address: { H_No: "", city: "", state: "", country: "" },
      password: "",
      confirm_password: "",
      role: "TP-Interviewer",
      status: "InActive",
      activation_status: "Pending",
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      toast.error(validationError);
      return;
    }
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        password: formData.password,
        confirm_password: formData.confirm_password,
      }).toString();

      const response = await aiDishaApi.post(
        `/interviewer_register?${queryParams}`,
        {
          name: formData.name,
          email: formData.email,
          mobile: formData.mobile,
          Date_of_birth: formData.Date_of_birth, // ← THIS IS ALREADY "1999-08-20" → PERFECT!
          address: formData.address,
          role: formData.role,
          status: formData.status,
          activation_status: formData.activation_status,
        }
      );

      toast.success(response.data.message || "Registration successful!");
      clearForm();
      navigate("/otpVerification", {
        state: { email: formData.email, source: "interviewerRegister" },
      });
    } catch (error) {
      console.error(error);
      const errMsg =
        error.response?.data?.detail?.[0]?.msg ||
        error.response?.data?.detail ||
        "Registration failed. Please try again.";
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4" style={{ maxWidth: "700px" }}>
      <div
        className="card"
        style={{ backgroundColor: "white", borderRadius: "8px" }}
      >
        <div
          className="card-header"
          style={{
            backgroundColor: "#033538",
            color: "white",
            borderRadius: "8px 8px 0 0",
            textAlign: "center",
          }}
        >
          <h5 className="mb-0" style={{ fontFamily: "Poppins" }}>
            Interviewer Registration
          </h5>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="card-body">
            {/* Name */}
            <div className="mb-3">
              <label
                className="form-label"
                style={{ color: "#033538", fontWeight: 500 }}
              >
                Full Name *
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter Full Name (e.g., John Doe)"
                autoComplete="name"
                required
              />
            </div>

            {/* Email and Mobile */}
            <div className="row">
              <div className="col-md-6 mb-3">
                <label
                  className="form-label"
                  style={{ color: "#033538", fontWeight: 500 }}
                >
                  Email *
                </label>
                <input
                  type="email"
                  className="form-control form-control-sm"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="name@example.com"
                  autoComplete="email"
                  required
                />
              </div>
              <div className="col-md-6 mb-3">
                <label
                  className="form-label"
                  style={{ color: "#033538", fontWeight: 500 }}
                >
                  Mobile *
                </label>
                <input
                  type="tel"
                  className="form-control form-control-sm"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  placeholder="10-digit Indian Mobile Number"
                  autoComplete="tel"
                  required
                />
              </div>
            </div>

            {/* DOB with Calendar Picker */}
            <div className="mb-3">
              <label
                className="form-label"
                style={{ color: "#033538", fontWeight: 500 }}
              >
                Date of Birth *
              </label>
              <input
                type="date"
                className="form-control form-control-sm"
                name="Date_of_birth"
                value={formData.Date_of_birth}
                onChange={handleChange}
                max={todayStr}
                required
                // Remove placeholder completely - it doesn't work reliably
              />

              {/* This is the magic: clear, visible, always correct format hint */}
              <div className="form-text text-muted mt-1">
                <small>
                  <i className="bi bi-calendar-event me-1"></i>
                  Please select your date of birth (e.g.,{" "}
                  <strong>20/08/1999</strong>)
                </small>
              </div>

              {/* Optional: Show selected date in Indian format for confirmation */}
              {formData.Date_of_birth && (
                <small className="text-success d-block mt-1">
                  Selected:{" "}
                  {new Date(formData.Date_of_birth).toLocaleDateString("en-GB")}
                  {/* Shows 20/08/1999 */}
                </small>
              )}
            </div>

            {/* Address */}
            <h6
              style={{
                color: "#033538",
                fontWeight: "bold",
                textAlign: "center",
                marginTop: "10px",
              }}
            >
              Address
            </h6>
            <div className="row">
              {["H_No", "city", "state", "country"].map((field, i) => (
                <div className="col-md-6 mb-3" key={i}>
                  <input
                    type="text"
                    placeholder={
                      field === "H_No"
                        ? "House No"
                        : field.charAt(0).toUpperCase() + field.slice(1)
                    }
                    className="form-control form-control-sm"
                    name={field}
                    value={formData.address[field]}
                    onChange={handleChange}
                    required
                  />
                </div>
              ))}
            </div>

            {/* Password Fields */}
            <div className="row">
              <div className="col-md-6 mb-3">
                <label
                  className="form-label"
                  style={{ color: "#033538", fontWeight: 500 }}
                >
                  Password *
                </label>
                <div className="input-group input-group-sm">
                  <input
                    type={showPassword ? "text" : "password"}
                    className="form-control"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Min. 6 Characters"
                    autoComplete="new-password"
                    required
                  />
                  <span className="input-group-text bg-black">
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </span>
                </div>
              </div>

              <div className="col-md-6 mb-3">
                <label
                  className="form-label"
                  style={{ color: "#033538", fontWeight: 500 }}
                >
                  Confirm Password *
                </label>
                <div className="input-group input-group-sm">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    className="form-control"
                    name="confirm_password"
                    value={formData.confirm_password}
                    onChange={handleChange}
                    placeholder="Re-enter Password"
                    autoComplete="new-password"
                    required
                  />
                  <span className="input-group-text bg-black">
                    <IconButton
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      edge="end"
                      size="small"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="card-footer d-flex justify-content-end border-0">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm me-2"
              onClick={clearForm}
              disabled={loading}
            >
              Clear
            </button>
            <button
              type="submit"
              className="btn btn-sm"
              style={{ backgroundColor: "#033538", color: "white" }}
              disabled={loading}
            >
              {loading ? "Registering..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InterviewerJoining;
